import { type SerializedEditorState } from 'lexical'
import { type EmailVariable, type SlotAttrs, type SlotFormat, type TextStyles } from '../emailTemplateTypes'

export type LexNode = { type: string; children?: LexNode[]; [key: string]: unknown }
export type SlotJson = LexNode & SlotAttrs & { type: 'email-slot' }
export type EmailJson = LexNode & { type: 'email'; frame: string; variables: EmailVariable[]; textStyles: TextStyles }

export type IssueKind = 'variable' | 'content'

export type IssueRule = 'empty' | 'tooLong' | 'templateLogic'

export interface StateError {
  slotId: string
  message: string
  kind: IssueKind
  rule?: IssueRule
  variable?: string
  tokens?: string[]
}

type ContentIssue = Pick<StateError, 'message' | 'rule' | 'tokens'>

const FORMAT_BITS = 1 | 2 | 4 | 8
export const SAFE_URL = /^(https?:|mailto:)/i
const ALLOWED_TYPES: Record<SlotFormat, Set<string>> = {
  plain: new Set(['paragraph', 'text', 'variable']),
  rich: new Set(['paragraph', 'heading', 'text', 'variable', 'linebreak', 'list', 'listitem', 'link']),
}
const TOP_LEVEL_TYPES = new Set(['paragraph', 'list', 'heading'])
const BLOCK_TYPES = new Set(['email-slot', 'email-group', 'email-locked'])
const PLACEHOLDER = /\{\{\s*[a-z_][a-z0-9_]*\s*\}\}/g
export const TEMPLATE_TOKENS = ['{{', '}}', '{%', '{#']
const DOCUMENT_ERROR: StateError = { slotId: '', message: 'Not an email document.', kind: 'content' }

export const rootOf = (state: SerializedEditorState): LexNode | undefined => (state as unknown as { root?: LexNode }).root

export const descendants = (node: LexNode): LexNode[] => [node, ...(node.children ?? []).flatMap(descendants)]

export const emailOf = (state: SerializedEditorState): EmailJson | null => {
  const first = rootOf(state)?.children?.[0]
  return first?.type === 'email' ? (first as EmailJson) : null
}

const blocksIn = (node: LexNode): LexNode[] => (node.children ?? []).flatMap((child) => (child.type === 'email-group' ? [child, ...blocksIn(child)] : [child]))

export const slotsOf = (state: SerializedEditorState): SlotJson[] => {
  const email = emailOf(state)
  return email ? (blocksIn(email).filter((block) => block.type === 'email-slot') as SlotJson[]) : []
}

export const documentError = (state: SerializedEditorState): StateError | null => {
  const email = emailOf(state)
  if (rootOf(state)?.children?.length !== 1 || !email) {
    return DOCUMENT_ERROR
  }
  const valid = email.children?.[0]?.type === 'email-slot' && blocksIn(email).every((block) => BLOCK_TYPES.has(block.type))
  return valid ? null : DOCUMENT_ERROR
}

const slotText = (slot: LexNode): string =>
  (slot.children ?? [])
    .flatMap(descendants)
    .map((node) => (node.type === 'text' ? String(node.text ?? '') : node.type === 'variable' ? `{{ ${String(node.variable)} }}` : ''))
    .join('')

const tokenList = (tokens: string[]): string => (tokens.length > 1 ? `${tokens.slice(0, -1).join(', ')} and ${tokens[tokens.length - 1]}` : tokens.join(''))

export const removeTokensMessage = (tokens: string[]): string => `Remove ${tokenList(tokens)}. Use Insert variable to add a variable.`

export const UNAVAILABLE_VARIABLE_MESSAGE = "Remove the highlighted variable. It can't be used in this email."

const contentErrors = (slot: SlotJson): ContentIssue[] => {
  const children = slot.children ?? []
  const allowed = ALLOWED_TYPES[slot.slotFormat]
  const shapeErrors = [
    ...(slot.slotFormat === 'plain' && (children.length !== 1 || children[0].type !== 'paragraph') ? ['Keep this field to one line.'] : []),
    ...children.filter((child) => !TOP_LEVEL_TYPES.has(child.type)).map((child) => `Remove the "${child.type}" content from this field.`),
  ]
  const nodeErrors = children.flatMap(descendants).flatMap((node): string[] => {
    if (!allowed.has(node.type)) {
      return [`Remove the "${node.type}" content from this field.`]
    }
    switch (node.type) {
      case 'text':
      case 'variable': {
        const bits = Number(node.format) || 0
        const formatOk = slot.slotFormat === 'plain' ? bits === 0 : (bits & ~FORMAT_BITS) === 0
        return [...(formatOk ? [] : ["Remove the text formatting this field doesn't allow."]), ...(node.style ? ['Remove the inline styles.'] : [])]
      }
      case 'link': {
        const url = String(node.url ?? '')
        return [
          ...(SAFE_URL.test(url) ? [] : ['Use a link that starts with http, https or mailto.']),
          ...(/[{}]/.test(url) ? ['Remove { and } from the link.'] : []),
        ]
      }
      case 'heading':
        return node.tag === 'h3' ? [] : ['Change this heading to Heading or Normal text.']
      case 'list':
        return node.listType === 'bullet' || node.listType === 'number' ? [] : ['Use a bulleted or numbered list.']
      default:
        return []
    }
  })
  const text = slotText(slot)
  const typedText = text.replace(PLACEHOLDER, '')
  const tokens = TEMPLATE_TOKENS.filter((token) => typedText.includes(token))
  const textErrors: ContentIssue[] = [
    ...(slot.required && text.trim() === '' ? [{ message: 'Fill in this field.', rule: 'empty' as const }] : []),
    ...(text.length > slot.maxLength ? [{ message: 'Shorten this field.', rule: 'tooLong' as const }] : []),
    ...(tokens.length ? [{ message: removeTokensMessage(tokens), rule: 'templateLogic' as const, tokens }] : []),
  ]
  return [...[...shapeErrors, ...nodeErrors].map((message) => ({ message })), ...textErrors]
}

const unavailableVariables = (slot: SlotJson, insertable: Set<string>): StateError[] =>
  Array.from(
    new Set(
      (slot.children ?? [])
        .flatMap(descendants)
        .filter((node) => node.type === 'variable' && !insertable.has(String(node.variable)))
        .map((node) => String(node.variable)),
    ),
  ).map((variable) => ({ slotId: slot.slotId, message: UNAVAILABLE_VARIABLE_MESSAGE, kind: 'variable', variable }))

const issuesOf = (slot: SlotJson, issues: ContentIssue[]): StateError[] =>
  issues
    .filter((issue, index) => issues.findIndex((other) => other.message === issue.message) === index)
    .map((issue) => ({ slotId: slot.slotId, kind: 'content', ...issue }))

export const validateState = (state: SerializedEditorState): StateError[] => {
  const document = documentError(state)
  if (document) {
    return [document]
  }
  const insertable = new Set((emailOf(state)?.variables ?? []).filter((variable) => variable.insertable).map((variable) => variable.name))
  return slotsOf(state).flatMap((slot) => [...issuesOf(slot, contentErrors(slot)), ...unavailableVariables(slot, insertable)])
}
